/**
 * DYNAMIC PARAMETER FIELDS COMPONENT
 * 
 * Componente reutilizável para renderizar campos de parâmetros dinâmicos
 * Localizado em shared/ui para uso em múltiplos domínios
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from './input';
import { Label } from './label';
import { Textarea } from './textarea';
import { Button } from './button';
import { Badge } from './badge';
import { Switch } from './switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Card, CardContent } from './card';
import { Eye, EyeOff, Settings, Lock, Shield } from 'lucide-react';
import { cn } from '@/infrastructure/lib/utils';

// Interfaces
interface SecretConfig {
  auth_type?: 'basic' | 'bearer' | 'apikey' | 'custom';
  auth_username?: string;
}

interface ParameterField {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'select' | 'textarea' | 'password' | 'secret';
  label: string;
  description?: string;
  required?: boolean;
  default?: unknown;
  options?: Array<{
    label: string;
    value: string;
  }>;
  placeholder?: string;
  value?: unknown;
  /** Show this field only when another field has a truthy value. E.g. "send_transcript" */
  showIf?: string;
  /** Configuration for secret fields — defines auth type and default username */
  secret_config?: SecretConfig;
}

interface ParameterDefinition {
  fields?: ParameterField[];
  values?: { [key: string]: unknown };
}

interface DynamicParameterFieldsProps {
  parameters: ParameterDefinition;
  values: { [key: string]: unknown };
  onChange: (values: { [key: string]: unknown }) => void;
  className?: string;
  selectedIntegrations?: Record<string, Record<string, unknown>[]>; // Integrações selecionadas no wizard
}

export const DynamicParameterFields: React.FC<DynamicParameterFieldsProps> = ({
  parameters,
  values,
  onChange,
  className,
  selectedIntegrations
}) => {
  const { t } = useTranslation();
  const [showPasswords, setShowPasswords] = React.useState<{ [key: string]: boolean }>({});
  const [touchedFields, setTouchedFields] = React.useState<{ [key: string]: boolean }>({});

  // Auto-persist defaults that the user can see in the UI but never clicks.
  // Without this the wizard sends `parameterValues` missing those fields and
  // the backend / engine never receives them (e.g. webhook_auth.auth_username
  // displayed as "prodgy" placeholder is just a visual default; if the user
  // doesn't type, it stays undefined in state and the installer workflow
  // can't resolve `{{parameters.webhook_auth.auth_username}}`).
  //
  // Seed exactly once per `parameters.fields` identity: a `useRef` keyed on
  // the `fields` array keeps the effect idempotent even when the parent
  // re-creates the `parameters` object on every render (a previous version
  // keyed on `parameters` alone could re-seed after the parent rebuilt its
  // state and briefly cleared `values`). When `parameters.fields` genuinely
  // changes (e.g. the wizard loads a different agent), we reset the guard
  // so the new set of defaults gets applied.
  const lastSeededFieldsRef = React.useRef<ParameterField[] | null>(null);
  React.useEffect(() => {
    const fields = parameters?.fields;
    if (!fields || fields.length === 0) return;
    if (lastSeededFieldsRef.current === fields) return;
    lastSeededFieldsRef.current = fields;

    const updates: Record<string, unknown> = {};
    for (const field of fields) {
      if (field.type === 'secret') {
        const cfg = field.secret_config;
        if (!cfg) continue;
        const existing = values[field.name];
        const existingObj =
          typeof existing === 'object' && existing !== null
            ? (existing as Record<string, unknown>)
            : {};
        const seeded: Record<string, unknown> = { ...existingObj };
        let changed = false;
        if (!seeded.auth_type && cfg.auth_type) {
          seeded.auth_type = cfg.auth_type;
          changed = true;
        }
        if (!seeded.auth_username && cfg.auth_username) {
          seeded.auth_username = cfg.auth_username;
          changed = true;
        }
        if (changed) {
          updates[field.name] = seeded;
        }
      } else if (!(field.name in values) && field.default !== undefined) {
        updates[field.name] = field.default;
      }
    }

    if (Object.keys(updates).length > 0) {
      onChange({ ...values, ...updates });
    }
    // `values` and `onChange` are intentionally excluded — the effect is
    // driven by the shape of `parameters.fields`, not by each keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameters?.fields]);

  // 🔍 Verificar se um campo tem valor auto-preenchido de integration_base.settings
  const getAutoFilledValue = (fieldName: string): { value: unknown; isAutoFilled: boolean } => {
    if (!selectedIntegrations) {
      return { value: null, isAutoFilled: false };
    }

    // Percorrer todas as integrações selecionadas
    for (const integrationList of Object.values(selectedIntegrations)) {
      if (!Array.isArray(integrationList)) continue;
      
      for (const integration of integrationList) {
        // Verificar se integration.settings tem o campo compatível
        const settings = integration?.settings as Record<string, unknown> | undefined;
        if (settings && typeof settings === 'object') {
          if (fieldName in settings) {
            let value = settings[fieldName];
            
            // Retornar apenas se o valor não for undefined/null/vazio
            if (value !== undefined && value !== null && value !== '') {
              // Normalizar URLs: remover barra final se existir
              if (typeof value === 'string' && value.match(/^https?:\/\/.+\/$/)) {
                value = value.replace(/\/$/, '');
              }
              
              return { value, isAutoFilled: true };
            }
          }
        }
      }
    }

    return { value: null, isAutoFilled: false };
  };

  // 🔄 Atualizar valor de um campo
  const handleFieldChange = (fieldName: string, value: unknown) => {
    // Marcar campo como tocado
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));

    const updatedValues = {
      ...values,
      [fieldName]: value
    };
    onChange(updatedValues);
  };

  // 📝 Marcar campo como tocado (ao perder foco)
  const handleFieldBlur = (fieldName: string) => {
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  // 👁️ Toggle visibilidade de senha
  const togglePasswordVisibility = (fieldName: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  // 👁️ Check if a field is visible based on showIf condition
  const isFieldVisible = (field: ParameterField): boolean => {
    if (!field.showIf) return true;
    const depValue = values[field.showIf];
    return !!(depValue && depValue !== 'false' && depValue !== '0');
  };

  // 🎯 Renderizar campo individual
  const renderField = (field: ParameterField) => {
    const { value: autoFilledValue, isAutoFilled } = getAutoFilledValue(field.name);
    const fieldValue = isAutoFilled ? autoFilledValue : (values[field.name] ?? field.value ?? field.default ?? '');
    const isRequired = field.required && isFieldVisible(field);
    const isEmpty = fieldValue === '' || fieldValue === null || fieldValue === undefined;
    const isTouched = touchedFields[field.name] || false;
    const showError = isRequired && isEmpty && isTouched && !isAutoFilled;

    switch (field.type) {
      case 'string':
        return (
          <div className="space-y-2">
            <Label htmlFor={field.name} className="flex items-center gap-2">
              {field.label}
              {isRequired && <span className="text-xs text-red-500">*</span>}
              {isAutoFilled && (
                <Badge variant="secondary" className="ml-2 text-xs flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  {t('dynamicParameterFields.autoFilled')}
                </Badge>
              )}
            </Label>
            {field.description && (
              <p className="text-xs text-muted-foreground">
                {field.description}
              </p>
            )}
            <Input
              id={field.name}
              type="text"
              placeholder={field.placeholder || `Digite ${field.label.toLowerCase()}`}
              value={String(fieldValue ?? '')}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              onBlur={() => handleFieldBlur(field.name)}
              disabled={isAutoFilled}
              className={cn(
                showError && "border-red-300 focus:border-red-500",
                isAutoFilled && "bg-muted cursor-not-allowed opacity-75"
              )}
            />
            {showError && (
              <p className="text-xs text-red-500">{t('dynamicParameterFields.required')}</p>
            )}
          </div>
        );

      case 'password':
        return (
          <div className="space-y-2">
            <Label htmlFor={field.name} className="flex items-center gap-2">
              {field.label}
              {isRequired && <span className="text-xs text-red-500">*</span>}
            </Label>
            {field.description && (
              <p className="text-xs text-muted-foreground">
                {field.description}
              </p>
            )}
            <div className="relative">
              <Input
                id={field.name}
                type={showPasswords[field.name] ? "text" : "password"}
                placeholder={field.placeholder || `Digite ${field.label.toLowerCase()}`}
                value={String(fieldValue ?? '')}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                className={cn(
                  "pr-10",
                  isRequired && isEmpty && "border-red-300 focus:border-red-500"
                )}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => togglePasswordVisibility(field.name)}
              >
                {showPasswords[field.name] ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
            {isRequired && isEmpty && (
              <p className="text-xs text-red-500">{t('dynamicParameterFields.required')}</p>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div className="space-y-2">
            <Label htmlFor={field.name} className="flex items-center gap-2">
              {field.label}
              {isRequired && <span className="text-xs text-red-500">*</span>}
              {isAutoFilled && (
                <Badge variant="secondary" className="ml-2 text-xs flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  {t('dynamicParameterFields.autoFilled')}
                </Badge>
              )}
            </Label>
            {field.description && (
              <p className="text-xs text-muted-foreground">
                {field.description}
              </p>
            )}
            <Textarea
              id={field.name}
              placeholder={field.placeholder || `Digite ${field.label.toLowerCase()}`}
              value={String(fieldValue ?? '')}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              onBlur={() => handleFieldBlur(field.name)}
              disabled={isAutoFilled}
              className={cn(
                showError && "border-red-300 focus:border-red-500",
                isAutoFilled && "bg-muted cursor-not-allowed opacity-75"
              )}
              rows={3}
            />
            {showError && (
              <p className="text-xs text-red-500">{t('dynamicParameterFields.required')}</p>
            )}
          </div>
        );

      case 'number':
        return (
          <div className="space-y-2">
            <Label htmlFor={field.name} className="flex items-center gap-2">
              {field.label}
              {isRequired && <span className="text-xs text-red-500">*</span>}
            </Label>
            {field.description && (
              <p className="text-xs text-muted-foreground">
                {field.description}
              </p>
            )}
            <Input
              id={field.name}
              type="number"
              placeholder={field.placeholder || "0"}
              value={String(fieldValue ?? '')}
              onChange={(e) => handleFieldChange(field.name, Number(e.target.value))}
              onBlur={() => handleFieldBlur(field.name)}
              className={cn(showError && "border-red-300 focus:border-red-500")}
            />
            {showError && (
              <p className="text-xs text-red-500">{t('dynamicParameterFields.required')}</p>
            )}
          </div>
        );

      case 'boolean':
        return (
          <div className="space-y-2">
            <div className="flex items-center space-x-3">
              <Switch
                id={field.name}
                checked={!!fieldValue}
                onCheckedChange={(checked) => handleFieldChange(field.name, checked)}
              />
              <div className="space-y-0.5">
                <Label htmlFor={field.name} className="flex items-center gap-2">
                  {field.label}
                  {isRequired && <span className="text-xs text-red-500">*</span>}
                </Label>
                {field.description && (
                  <p className="text-xs text-muted-foreground">
                    {field.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 'select':
        return (
          <div className="space-y-2">
            <Label htmlFor={field.name} className="flex items-center gap-2">
              {field.label}
              {isRequired && <span className="text-xs text-red-500">*</span>}
              {isAutoFilled && (
                <Badge variant="secondary" className="ml-2 text-xs flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  {t('dynamicParameterFields.autoFilled')}
                </Badge>
              )}
            </Label>
            {field.description && (
              <p className="text-xs text-muted-foreground">
                {field.description}
              </p>
            )}
            <Select
              value={String(fieldValue ?? '')}
              onValueChange={(value) => handleFieldChange(field.name, value)}
              disabled={isAutoFilled}
            >
              <SelectTrigger className={cn(
                showError && "border-red-300 focus:border-red-500",
                isAutoFilled && "bg-muted cursor-not-allowed opacity-75"
              )}>
                <SelectValue placeholder={field.placeholder || `Selecione ${field.label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {showError && (
              <p className="text-xs text-red-500">{t('dynamicParameterFields.required')}</p>
            )}
          </div>
        );

      case 'secret': {
        // Secret field: auth type selector + dynamic credential fields
        // Values are stored as JSON: { auth_type, auth_username?, auth_secret }
        const secretValue = (typeof fieldValue === 'object' && fieldValue !== null)
          ? fieldValue as Record<string, string>
          : { auth_type: field.secret_config?.auth_type || 'basic', auth_secret: String(fieldValue || '') };
        const selectedAuthType = secretValue.auth_type || 'basic';

        const handleSecretChange = (subField: string, subValue: string) => {
          handleFieldChange(field.name, { ...secretValue, [subField]: subValue });
        };

        // Mirror the wizard's required-secret validation so the field surfaces
        // WHY "Next"/"Install" is blocked instead of failing silently. The
        // credential always lives in auth_secret; basic also needs a username,
        // custom also needs a header name; none needs nothing.
        const filled = (v?: string): boolean => !!v && v.trim() !== '';
        const secretComplete =
          selectedAuthType === 'none'
            ? true
            : selectedAuthType === 'basic'
              ? filled(secretValue.auth_username) && filled(secretValue.auth_secret)
              : selectedAuthType === 'custom'
                ? filled(secretValue.custom_header_name) && filled(secretValue.auth_secret)
                : filled(secretValue.auth_secret);
        const showSecretError = !!isRequired && !secretComplete && isTouched;

        return (
          <Card className="border-blue-200 bg-blue-50/30">
            <CardContent className="pt-4 space-y-3">
              <Label className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-blue-600" />
                {field.label}
                {isRequired && <span className="text-xs text-red-500">*</span>}
              </Label>
              {field.description && (
                <p className="text-xs text-muted-foreground">{field.description}</p>
              )}

              {/* Auth Type Selector */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">
                  {t('dynamicParameterFields.authType', 'Authentication Type')}
                </Label>
                <Select
                  value={selectedAuthType}
                  onValueChange={(v) => handleSecretChange('auth_type', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic Auth (Username + Password)</SelectItem>
                    <SelectItem value="bearer">Bearer Token</SelectItem>
                    <SelectItem value="apikey">API Key</SelectItem>
                    <SelectItem value="custom">Custom Header</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Dynamic fields based on auth type */}
              {(selectedAuthType === 'basic') && (
                <div className="space-y-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {t('dynamicParameterFields.username', 'Username')}
                    </Label>
                    <Input
                      value={secretValue.auth_username || field.secret_config?.auth_username || ''}
                      onChange={(e) => handleSecretChange('auth_username', e.target.value)}
                      placeholder="prodgy"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {t('dynamicParameterFields.password', 'Password')}
                    </Label>
                    <div className="relative">
                      <Input
                        type={showPasswords[field.name] ? "text" : "password"}
                        value={secretValue.auth_secret || ''}
                        onChange={(e) => handleSecretChange('auth_secret', e.target.value)}
                        onBlur={() => handleFieldBlur(field.name)}
                        placeholder={field.placeholder || ''}
                        className={cn("pr-10", showSecretError && "border-red-300 focus:border-red-500")}
                      />
                      <Button
                        type="button" variant="ghost" size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => togglePasswordVisibility(field.name)}
                      >
                        {showPasswords[field.name] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {(selectedAuthType === 'bearer' || selectedAuthType === 'apikey') && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {selectedAuthType === 'bearer' ? 'Token' : 'API Key'}
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPasswords[field.name] ? "text" : "password"}
                      value={secretValue.auth_secret || ''}
                      onChange={(e) => handleSecretChange('auth_secret', e.target.value)}
                      onBlur={() => handleFieldBlur(field.name)}
                      placeholder={field.placeholder || ''}
                      className={cn("pr-10", showSecretError && "border-red-300 focus:border-red-500")}
                    />
                    <Button
                      type="button" variant="ghost" size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => togglePasswordVisibility(field.name)}
                    >
                      {showPasswords[field.name] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}

              {selectedAuthType === 'custom' && (
                <div className="space-y-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {t('dynamicParameterFields.headerName', 'Header Name')}
                    </Label>
                    <Input
                      value={secretValue.custom_header_name || ''}
                      onChange={(e) => handleSecretChange('custom_header_name', e.target.value)}
                      placeholder="X-Webhook-Secret"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {t('dynamicParameterFields.headerValue', 'Header Value')}
                    </Label>
                    <div className="relative">
                      <Input
                        type={showPasswords[field.name] ? "text" : "password"}
                        value={secretValue.auth_secret || ''}
                        onChange={(e) => handleSecretChange('auth_secret', e.target.value)}
                        onBlur={() => handleFieldBlur(field.name)}
                        placeholder={field.placeholder || ''}
                        className={cn("pr-10", showSecretError && "border-red-300 focus:border-red-500")}
                      />
                      <Button
                        type="button" variant="ghost" size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => togglePasswordVisibility(field.name)}
                      >
                        {showPasswords[field.name] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {showSecretError && (
                <p className="text-xs text-red-500">{t('dynamicParameterFields.required')}</p>
              )}

              <p className="text-xs text-blue-600 flex items-center gap-1">
                <Lock className="h-3 w-3" />
                {t('dynamicParameterFields.secretNote', 'Credentials are encrypted and stored securely')}
              </p>
            </CardContent>
          </Card>
        );
      }

      default:
        return (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-sm text-yellow-800">
              ⚠️ {t('dynamicParameterFields.notSupportedField')} {field.type}
            </p>
          </div>
        );
    }
  };

  // 📋 Se não há campos definidos
  if (!parameters?.fields || parameters.fields.length === 0) {
    return (
      <div className={cn("space-y-4", className)}>
        <Card className="border-dashed">
          <CardContent className="p-6 text-center">
            <Settings className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {t('dynamicParameterFields.parametersNotNecessary')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* 📝 Campos */}
      <div className="space-y-4">
        {parameters.fields.map((field) => {
          if (!isFieldVisible(field)) return null;
          return (
            <Card key={field.name} className="p-0">
              <CardContent className="p-4">
                {renderField(field)}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* 📈 Resumo de Validação */}
      <div className="text-xs text-muted-foreground">
        {parameters.fields.filter(f => f.required && isFieldVisible(f)).length > 0 && (
          <p>
            * {t('dynamicParameterFields.required')}: {parameters.fields.filter(f => f.required && isFieldVisible(f)).length} {t('common.of')} {parameters.fields.filter(f => isFieldVisible(f)).length}
          </p>
        )}
      </div>
    </div>
  );
};
